import React, {
    Fragment,
    useCallback,
    useState,
    useEffect,
    useMemo,
} from 'react';
import {
    Routes,
    Route,
    Link,
    useNavigate,
    Navigate,
    useParams,
} from 'react-router-dom';
import { observer } from 'mobx-react';
import { Button } from 'reactstrap';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import SubjectView from './SubjectView';
import ThemedFooterButton from '../../components/themed/ThemedFooterButton';
import ShortSubjectsList from '../../components/training/ShortSubjectsList';
import classes from 'classnames';
import { useStores } from '../../hooks/use-stores';
import TrainingCompleted from '../Trainings/TrainingCompleted';
import {
    FlexDiv,
    Heading4,
    TextDefault,
    TextLabel,
} from '@awarego-ui/components';
import ViewHeader from '../../components/ViewHeader';
import MenuIcon from '@material-ui/icons/Menu';
import MenuOpenIcon from '@material-ui/icons/MenuOpen';
import LanguageSelector from './language-selector';

export default observer(
    ({
        parentUrl,
        training,
        subjectId,
        trainingStore,
        store,
        videoTokenStore,
        onClose,
        isDemo,
        isScorm,
        availableLanguages,
        selectedLanguage,
        onSelectLanguage,
    }) => {
        const { t } = useTranslation();
        let navigate = useNavigate();

        const [isFinished, setIsFinished] = useState(false);
        const [showMobileMenu, setShowMobileMenu] = useState(false);

        const {
            brandingStore,
        } = useStores();

        const { brandingLoaded, account_name, logo, trainingSkipAutoFinish } =
            brandingStore;

        const subject = trainingStore.training.subjects.find(
            (x) => x.id === subjectId
        );

        const showLessonMenu = () => {
            setShowMobileMenu(true);
        };
        const hideLessonMenu = () => {
            setShowMobileMenu(false);
        };

        const onNextLesson = async () => {
            await trainingStore.reloadTraining();
        };

        const handleClosePage = useCallback(() => {
            if (onClose) {
                return onClose();
            } else {
                navigate(parentUrl);
            }
        }, [history, onClose, parentUrl]);

        const onFinish = async () => {
            await trainingStore.reloadTraining();
            let lastNextEnabled = trainingStore.training.nextSubjectDate
                ? true
                : trainingStore.certificateQualified;

            console.log('onFinish: lastNextEnabled', lastNextEnabled);
            if (lastNextEnabled) setIsFinished(true);
        };

        const {
            certificateQualified,
            assessmentAvailable,
            availableUnfinishedSubjects,
            subjects,
        } = trainingStore;

        useEffect(() => {
            if (isScorm && certificateQualified) {
                if (!trainingSkipAutoFinish) setIsFinished(true);
            }
        }, [isScorm, certificateQualified]);

        let lastNextEnabled = training.nextSubjectDate
            ? true
            : certificateQualified;

        let score =
            training.subjectsSent > 0
                ? (100 * (training.viewed || 0)) / training.subjectsSent
                : 0;

        const trainingName = useMemo(() => {
            if (!training) return;
            if (isDemo) return 'Cybersecurity Awareness Training';
            return training.name;
        }, [training, isDemo]);

        const videosWatchedText = useMemo(() => {
            return (
                <Fragment>
                    {trainingStore.viewedVideos || training.viewed}/
                    {training.subjectsSent} {t('videos watched')}
                </Fragment>
            );
        }, [
            t,
            training.subjectsSent,
            training.viewed,
            trainingStore.viewedVideos,
        ]);

        const SubjectsIdRouteElement = () => {
            const index = _.findIndex(
                availableUnfinishedSubjects,
                (x) => x.id === Number(subjectId)
            );
            const isCompleted = index < 0;
            const isFirst = index === 0;
            const isLast = index === availableUnfinishedSubjects.length - 1;

            return (
                <Fragment>
                    {isFinished ? (
                        <div className="wizard-body">
                            <TrainingCompleted
                                training={training}
                                isDemo={isDemo}
                                store={store}
                                isScorm={isScorm}
                            />
                        </div>
                    ) : (
                        <div className="wizard-body">
                            <div
                                className={classes('sidebar ', {
                                    open: showMobileMenu,
                                })}
                                onClick={() => hideLessonMenu()}
                            >
                                <ShortSubjectsList
                                    certificateQualified={certificateQualified}
                                    assessment_id={training.assessment_id}
                                    assessmentAvailable={assessmentAvailable}
                                    onCertificateRequest={onFinish}
                                    subjects={training.subjects}
                                    parentUrl={parentUrl}
                                    selectedId={subjectId}
                                />
                                {subject && subject.type !== 2 && (
                                    <div className="btn-fixed">
                                        <Button className="btn-link">
                                            {t('Back to training')}
                                        </Button>
                                    </div>
                                )}
                            </div>
                            <div className={'wizard-content'}>
                              <SubjectView
                                        training={training}
                                        subject={subject}
                                        store={store}
                                        videoTokenStore={videoTokenStore}
                                        isDemo={isDemo}
                                        language={store.language}
                                    />
                            </div>
                        </div>
                    )}
                    {((subject && subject.type !== 2) || isCompleted) && (
                        <div className={'wizard-footer'}>
                            <div className={'actions'}>
                                {!isFirst && !isCompleted && (
                                    <Button
                                        tag={Link}
                                        className="mobile-arrow-back"
                                        color={'link'}
                                        size={'lg'}
                                        to={`${parentUrl}/subjects/${
                                            availableUnfinishedSubjects[index - 1]
                                                .id
                                        }`}
                                    >
                                        {t('Previous')}
                                    </Button>
                                )}
                                {!isLast &&
                                   (
                                        <ThemedFooterButton
                                            mode="primary"
                                            continue
                                            to={`${parentUrl}/subjects/${
                                                availableUnfinishedSubjects[index + 1]
                                                    .id
                                            }`}
                                            text={t('Next lesson')}
                                        ></ThemedFooterButton>
                                    )}
                                {(isLast) &&
                                    !isFinished && (
                                        <ThemedFooterButton
                                            mode="primary"
                                            disabled={
                                                availableUnfinishedSubjects.length >
                                                    0 || !lastNextEnabled
                                            }
                                            continue
                                            onClick={onFinish}
                                            text={t('Finish')}
                                        ></ThemedFooterButton>
                                    )}
                                {isLast && isFinished && (
                                    <ThemedFooterButton
                                        mode="primary"
                                        continue
                                        onClick={handleClosePage}
                                        text={t('Finish')}
                                    ></ThemedFooterButton>
                                )}
                            </div>
                        </div>
                    )}
                </Fragment>
            );
        };


        const SubjectsDefaultRedirectElement = () => {
            return training.subjects.length > 0 ? (
                <Navigate
                    to={`${parentUrl}/subjects/${
                        availableUnfinishedSubjects.length > 0
                            ? availableUnfinishedSubjects[0].id
                            : subjects[0].id
                    }`}
                />
            ) : null;
        };

        return (
            <div
                className={classes('wizard', {
                    scorm: subject && subject.type === 2,
                })}
            >
                <ViewHeader
                    onClose={handleClosePage}
                    isDemo={isDemo}
                    brandingLoaded={brandingLoaded}
                    logo={logo}
                    account_name={account_name}
                    done={score}
                    mobileMainHeading={<Heading4>{trainingName}</Heading4>}
                    mobileSubheading={
                        <TextLabel bold lighter>
                            {videosWatchedText}
                        </TextLabel>
                    }
                    mobileRightCorner={
                        <FlexDiv marginLeftAuto>
                            <FlexDiv $noWrap alignCenter gap={16}>
                                {availableLanguages && !brandingStore.hideLanguageList ? (
                                    <LanguageSelector
                                        selectedLanguage={selectedLanguage}
                                        handleSelectLanguage={onSelectLanguage}
                                        availableLanguages={availableLanguages}
                                    />
                                ) : null}
                                <FlexDiv>
                                    {!showMobileMenu ? (
                                        <MenuIcon onClick={showLessonMenu} />
                                    ) : (
                                        <MenuOpenIcon
                                            onClick={hideLessonMenu}
                                        />
                                    )}
                                </FlexDiv>
                            </FlexDiv>
                        </FlexDiv>
                    }
                    desktopMainHeading={trainingName}
                    desktopRightCorner={
                        <FlexDiv $noWrap alignCenter gap={16}>
                            {availableLanguages && !brandingStore.hideLanguageList ? (
                                <LanguageSelector
                                    selectedLanguage={selectedLanguage}
                                    handleSelectLanguage={onSelectLanguage}
                                    availableLanguages={availableLanguages}
                                />
                            ) : null}
                            <TextDefault bold lighter noWrap>
                                {videosWatchedText}
                            </TextDefault>
                        </FlexDiv>
                    }
                />
                {subjectId ? <SubjectsIdRouteElement /> : <SubjectsDefaultRedirectElement /> }
            </div>
        );
    }
);
